import React, { Component, ReactNode, useState } from 'react';
import { createBrowserRouter, Link, Outlet, RouterProvider, useRoutes } from 'react-router-dom';
import { appRoutes } from './marketplace/routes/appRoutes';
import DraftOutboxProvider from './marketplace/components/DraftOutboxProvider';
import RouteError from './marketplace/components/RouteError';
import SignedOutNotice from './marketplace/components/SignedOutNotice';
import UnverifiedBanner from './marketplace/components/UnverifiedBanner';
import { useDiscardUnsyncedDrafts } from './marketplace/hooks/useDraftOutbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Toaster } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { ChevronDown, Menu } from 'lucide-react';

// Navigation component
function MarketplaceNav() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Signing out discards this page's unsynced drafts, without the leave-page warning (FR-009d).
  const discardUnsyncedDrafts = useDiscardUnsyncedDrafts();

  const handleLinkClick = (path: string) => {
    console.log('Navigation clicked:', path);
    setMobileMenuOpen(false); // Close mobile menu on navigation
  };

  // Get user info from global config
  const user = window.MARKETPLACE_CONFIG?.user || {};

  return (
    <nav className="bg-card shadow-sm">
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex h-16 justify-between">
          <div className="flex items-center space-x-8">
            <a href="/" className="shrink-0">
              <img
                src="/static/brand/wordmark-main-quest-green.svg"
                alt="Main Quest"
                className="h-4 w-auto"
              />
            </a>

            <div className="hidden space-x-1 md:flex">
              <Link
                to="/my-collections"
                onClick={() => handleLinkClick('/my-collections')}
                className="rounded-full px-3 py-2 font-medium text-muted-foreground transition-colors duration-200 hover:bg-secondary hover:text-primary"
              >
                Collections
              </Link>
              <Link
                to="/collections/create"
                onClick={() => handleLinkClick('/collections/create')}
                className="rounded-full px-3 py-2 font-medium text-muted-foreground transition-colors duration-200 hover:bg-secondary hover:text-primary"
              >
                Create Collection
              </Link>
              <Link
                to="/listings/create"
                onClick={() => handleLinkClick('/listings/create')}
                className="rounded-full px-3 py-2 font-medium text-muted-foreground transition-colors duration-200 hover:bg-secondary hover:text-primary"
              >
                Add Listing
              </Link>
              <Link
                to="/my-listings"
                onClick={() => handleLinkClick('/my-listings')}
                className="rounded-full px-3 py-2 font-medium text-muted-foreground transition-colors duration-200 hover:bg-secondary hover:text-primary"
              >
                My Listings
              </Link>
              <Link
                to="/invitations"
                onClick={() => handleLinkClick('/invitations')}
                className="rounded-full px-3 py-2 font-medium text-muted-foreground transition-colors duration-200 hover:bg-secondary hover:text-primary"
              >
                Invitations
              </Link>
              <Link
                to="/conversations"
                onClick={() => handleLinkClick('/conversations')}
                className="rounded-full px-3 py-2 font-medium text-muted-foreground transition-colors duration-200 hover:bg-secondary hover:text-primary"
              >
                Messages
              </Link>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Desktop user menu */}
            <div className="hidden md:block">
              {user?.isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger className="flex items-center space-x-2 rounded-full px-3 py-2 text-muted-foreground transition-colors duration-200 hover:bg-secondary hover:text-primary focus:outline-none">
                    <span>Account</span>
                    <ChevronDown className="size-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel>{user?.username || 'User'}</DropdownMenuLabel>
                    <DropdownMenuItem asChild>
                      <Link to="/settings/account" className="cursor-pointer">
                        Account settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <a
                        href="/accounts/logout/"
                        onClick={discardUnsyncedDrafts}
                        className="cursor-pointer"
                      >
                        Logout
                      </a>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <a
                  href="/accounts/login/"
                  className="rounded-full px-3 py-2 font-medium text-muted-foreground transition-colors duration-200 hover:bg-secondary hover:text-primary"
                >
                  Login
                </a>
              )}
            </div>

            {/* Mobile menu button */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger className="p-2 text-muted-foreground hover:text-primary md:hidden">
                <Menu className="size-6" />
              </SheetTrigger>
              <SheetContent side="right" className="w-64">
                <SheetHeader>
                  <SheetTitle>Menu</SheetTitle>
                </SheetHeader>
                <div className="mt-6 flex flex-col space-y-4">
                  {user?.isAuthenticated && (
                    <div className="border-b pb-4">
                      <p className="text-sm font-medium">{user?.username || 'User'}</p>
                    </div>
                  )}

                  <Link
                    to="/my-collections"
                    onClick={() => handleLinkClick('/my-collections')}
                    className="rounded-md px-3 py-2 text-foreground transition-colors duration-200 hover:text-primary"
                  >
                    Collections
                  </Link>
                  <Link
                    to="/collections/create"
                    onClick={() => handleLinkClick('/collections/create')}
                    className="rounded-md px-3 py-2 text-foreground transition-colors duration-200 hover:text-primary"
                  >
                    Create Collection
                  </Link>
                  <Link
                    to="/listings/create"
                    onClick={() => handleLinkClick('/listings/create')}
                    className="rounded-md px-3 py-2 text-foreground transition-colors duration-200 hover:text-primary"
                  >
                    Add Listing
                  </Link>
                  <Link
                    to="/my-listings"
                    onClick={() => handleLinkClick('/my-listings')}
                    className="rounded-md px-3 py-2 text-foreground transition-colors duration-200 hover:text-primary"
                  >
                    My Listings
                  </Link>
                  <Link
                    to="/invitations"
                    onClick={() => handleLinkClick('/invitations')}
                    className="rounded-md px-3 py-2 text-foreground transition-colors duration-200 hover:text-primary"
                  >
                    Invitations
                  </Link>

                  {user?.isAuthenticated ? (
                    <div className="border-t pt-4">
                      <Link
                        to="/settings/account"
                        onClick={() => handleLinkClick('/settings/account')}
                        className="block rounded-md px-3 py-2 text-foreground transition-colors duration-200 hover:text-primary"
                      >
                        Account settings
                      </Link>
                      <a
                        href="/accounts/logout/"
                        onClick={discardUnsyncedDrafts}
                        className="block rounded-md px-3 py-2 text-foreground transition-colors duration-200 hover:text-primary"
                      >
                        Logout
                      </a>
                    </div>
                  ) : (
                    <div className="border-t pt-4">
                      <a
                        href="/accounts/login/"
                        className="block rounded-md px-3 py-2 text-foreground transition-colors duration-200 hover:text-primary"
                      >
                        Login
                      </a>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}

// Error Boundary Component
interface ErrorBoundaryState {
  hasError: boolean;
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Marketplace Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background">
          <div className="text-center">
            <h1 className="mb-4 text-2xl font-bold text-foreground">Something went wrong</h1>
            <p className="mb-4 text-muted-foreground">Please refresh the page and try again.</p>
            <Button className="rounded-full" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Each area owns its routes (`marketplace/routes/<area>.tsx`), so adding one
 * touches only that area's file (SC-009); `appRoutes` composes them.
 */
function AppRoutes() {
  return useRoutes(appRoutes);
}

/** The whole signed-in page: nav, banner, the areas' routes and the footer. */
function AppShell() {
  return (
    <DraftOutboxProvider>
      <div className="min-h-screen bg-background">
        <Toaster />
        <MarketplaceNav />

        {/* Message Display Area */}
        <div id="message-area" className="mx-auto max-w-7xl px-4 pt-4">
          {/* Messages will be displayed here via Django or React state */}
        </div>

        <main className="mx-auto max-w-7xl space-y-6 px-4 py-8">
          <SignedOutNotice />
          <UnverifiedBanner />
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="mt-12 bg-muted py-8 text-muted-foreground">
          <div className="mx-auto max-w-7xl px-4 text-center">
            <p>&copy; 2026 Main Quest</p>
          </div>
        </footer>
      </div>
    </DraftOutboxProvider>
  );
}

// Main App Component
export default function MarketplaceApp() {
  // A data router, so a screen can hold navigation while it would lose work
  // (`useBlocker`, New from photos); the areas' routes still compose below it.
  // A page that throws shows the app's error state inside the shell; the
  // outer errorElement covers the shell itself. Never the router's default.
  const [router] = useState(() =>
    createBrowserRouter([
      {
        element: <AppShell />,
        errorElement: <RouteError />,
        children: [{ path: '*', element: <AppRoutes />, errorElement: <RouteError /> }],
      },
    ]),
  );
  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
}
